import { autoinject, bindable, computedFrom, observable, type Disposable } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { BindingSignaler } from "aurelia-templating-resources";
import { ResizeObserver } from "@juggle/resize-observer";
import { MobileHelper } from "../../helpers/mobile-helper";
import { Reflection } from "../../helpers/reflection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../constants/event-messages.constants";
import { type TabModel } from "../../models/basic.models";
import { CacheClientService, CacheLocation} from "../../services/basic/client-caching.service";

//Load styles
require("./comp-tabs.scss");

@autoinject
@transient()
export class CompTabs {

    //Is the 'expandable menu' visible
    public isMenuDropdownVisible: boolean = false;
    //Is the menu (<nav>) inside the expandable menu visible
    public isMenuTabsDropdownVisible: boolean = false;
    //Tabs hidden from the main (horizontal) menu to be displayed inside the 'expandable' menu
    public hiddenTabs: { [index: number]: boolean } = {};
    public cachedWidths: { [index: number]: number } = {};

    //Menu tabs to be displayed
    @bindable
    public tabs: TabModel[];

    @bindable
    public checkErrorsFrequency: number = 500;

    //Currently selected/active menu tab
    @bindable
    public currentTab: TabModel;

    @bindable
    public cacheLastVisitedTab: boolean = true;

    @bindable
    public shouldRemoveCacheOnDetached: boolean = true;

    //Key is used to cache current (active) tab which belongs to a particular section within the platform i.e. Activities, Inspections etc.
    @bindable
    public currentStepKey: string = "currentStep";

    //The title key inside the 'tabs' bindable
    @bindable
    public titleKey: string = "title";

    //Horizontal offset to cater for 'show more' (expandable) menu
    @bindable
    public showMoreMenuWidthOffset: number = 30;

    //Main (horizontal) menu element reference
    @observable
    public navElement: HTMLElement;

    @bindable
    public theme: string = "primary";

    //Expandable menu element reference
    @observable
    public menuButtonElement: HTMLElement;

    private _resizeObserver: ResizeObserver = null;
    private _subscriptionToDocumentClickEvent: Disposable;
    //Main (horizontal) menu tab elements
    tabElements: HTMLElement[] = [];

    constructor(private CacheClientService: CacheClientService, private element: Element,
        private bindingSignaler: BindingSignaler, private eventAggregator: EventAggregator) {
        //Close 'show more' (expandable) menu on document click
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeMoreMenu);
    }

    @computedFrom(Reflection.getPropertyName<CompTabs>(t => t.currentTab))
    public get currentTabIndex(): number {
        return (this.tabs || []).indexOf(this.currentTab);
    }

    bind() {
        this.titleKeyChanged();
        this.tabsChanged();
    }

    detached() {
        //Remove 'current step' cached key
        if (this.shouldRemoveCacheOnDetached && this.cacheLastVisitedTab) {
            this.CacheClientService.remove(this.currentStepKey, CacheLocation.LocalStorage);
        }

        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }

        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
    }

    //Check if device width is mobile or not
    get getIsMobile(): boolean {
        return MobileHelper.isMobileDevice();
    }

    //Title key bindable changed handler
    titleKeyChanged() {
        setTimeout(() => {
            this.bindingSignaler.signal('update-title');
        });
    }

    //Tabs binding changed handler
    tabsChanged() {
        //If a 'current' tab is chosen
        if (this.currentTab) {
            this.goToTab(null, this.currentTab);
        }
        this.setTab();
        this.bindingSignaler.signal('update-title');
        setTimeout(() => {
            this.computeDropdown();
        });
    }

    /**
     * Get tab title
     * @param tab Menu tab
     */
    getTabTitle(tab: TabModel): string {
        return typeof (tab[this.titleKey]) == "function" ? tab[this.titleKey]() : (tab[this.titleKey] ? tab[this.titleKey] : (typeof tab == "string" ? tab : ''));
    }

    /**
     * Get the menu tab's 'show' property/result
     * @param tab Menu tab
     */
    getShow(tab: TabModel): boolean {
        if (tab)
            return "show" in tab ? (typeof (tab.show) == "function" ? tab.show() : tab.show) : true;
        else
            return true;
    }

    /**
     * Navigation element observable changed handler
     * @param newElement
     * @param oldElement
     */
    navElementChanged(newElement, oldElement) {
        if (newElement != null && newElement instanceof Element) {

            if (this._resizeObserver && oldElement) {
                this._resizeObserver.unobserve(oldElement);
            }

            this._resizeObserver = (this._resizeObserver || new ResizeObserver(() => {
                this.computeDropdown();
            }));

            setTimeout(() => {
                this._resizeObserver.observe(newElement);
            },100)
        }
    }

    /**
     * Menu button element observable changed handler
     * @param newElement
     * @param oldElement
     */
    menuButtonElementChanged(newElement, oldElement) {
        if (newElement != null && newElement instanceof Element && oldElement != null) {

            if (this._resizeObserver && oldElement != null) {
                this._resizeObserver.unobserve(oldElement);
            }

            this._resizeObserver = (this._resizeObserver || new ResizeObserver(() => {
                this.computeDropdown();
            }));

            this._resizeObserver.observe(this.menuButtonElement);
        }
    }

    //Set current tab
    public goToTab(event: any, tab: any) {
        if (event)
            event.stopPropagation();

        if (!tab) {
            return;
        }

        //Is freemium
        if(tab.lockedFeature) {
            tab.unlockFeature();
            return;
        }

        const tabChangedCallback = () => {
            if (this.currentTab !== tab) {
                this.currentTab = tab;
                if (this.cacheLastVisitedTab) {
                    //Cache the current step
                    this.CacheClientService.put(this.currentStepKey, this.currentTab.step, CacheLocation.LocalStorage);
                }
                //scroll into view
                if (this.currentTab && this.tabs && this.tabElements?.length > 0) {
                    const currentTabIndex = this.tabs.findIndex((tab: TabModel) => tab == this.currentTab);
                    if (currentTabIndex >= 0) {
                        const currentTabElement = this.tabElements[currentTabIndex];
                        if (this.navElement) {
                            this.navElement.scrollLeft = currentTabElement ? currentTabElement.offsetLeft : 0;
                        }
                    }
                }
                //Publish current tab change event
                setTimeout(() => {
                    this.eventAggregator.publish("tab-changed", this.currentTab);

                    this.element.dispatchEvent(
                        new CustomEvent("tab-changed",
                            {
                                bubbles: true,
                                detail: this.currentTab
                            })
                    );
                    this.bindingSignaler.signal("tab-changed");
                });
            }
            this.isMenuTabsDropdownVisible = false;
        }

        //If tab has a 'click' event handler
        if (tab.onClick) {
            const clickResult = tab.onClick(this);

            //if false is returned tab should not change
            if (clickResult === false) return;

            if (clickResult instanceof Promise) {
                //If an error is thrown from the click response
                clickResult.then(tabChangedCallback).catch(e => {
                    if(e) 
                        console.error(e);
                });
            } else {
                tabChangedCallback();
            }
        } else {
            tabChangedCallback();
        }
    }

    //Set current tab if no current tab exists
    private setTab() {
        //If cache last visited tab, and there is no current tab
        if (this.cacheLastVisitedTab && !this.currentTab) {
            //Get cached current tab step
            this.CacheClientService.get(this.currentStepKey, CacheLocation.LocalStorage, false).then(step => {
                if(!this.currentTab)//check again since by time cache is loaded current tab may be set
                {
                    //Set current tab
                    if (step) {
                        const tab = Enumerable.from(this.tabs).firstOrDefault((tab: TabModel) => this.getShow(tab) && tab.step == step);

                        if (tab) {
                            this.goToTab(null, tab);
                        } else  {
                            this.goToTab(null, Enumerable.from(this.tabs).firstOrDefault((tab: TabModel) => this.getShow(tab)));
                        }
                    }
                    else {
                        this.goToTab(null, Enumerable.from(this.tabs).firstOrDefault((tab: TabModel) => this.getShow(tab)));
                    }
                }
            });
        }
    }

    /**
     * Get Menu tab error count
     * @param tab Menu tab
     */
    getErrorCount(tab: any) {
        if (tab.element) {

            //Changed the class from "error-validation" to "error-validation-tabs" because of DEL-12832
            return $(tab.element).find(".error-validation-tabs").length;
        }
        if (tab.errorsCount > 0) {
            return tab.errorsCount;
        }
        return 0;
    }

    //Compute which tabs are to be hidden in expandable menu and which not
    private computeDropdown() {
        const hiddenTabs: { [index: number]: boolean } = {};
        let isMenuDropdownVisible = false;

        //If device is desktop
        if (!MobileHelper.isMobileDevice()) {
            const navElementContainer = this.navElement;
            const tabs = this.tabElements;

            if (!navElementContainer || !tabs) return;

            const cachedWidths: { [index: number]: number } = {};
            const navContainerRect = navElementContainer.getBoundingClientRect();

            //Offset keeps track of the horizontal offset of individual tabs (from left side of screen)
            let tabOffset = Number(this.showMoreMenuWidthOffset);

            tabs.forEach((tab: HTMLElement, tabIndex: number) => {
                if (tab) {
                    const tabRect = tab.getBoundingClientRect();
                    let tabWidth = tabRect.width;
                    if ((!tabWidth || tabWidth === 0) && this.cachedWidths[tabIndex] > 0) {
                        tabWidth = !!this.cachedWidths[tabIndex] && this.cachedWidths[tabIndex] > 0 ? this.cachedWidths[tabIndex]: tabWidth;
                    }

                    //Cache tab width (to avoid re-computation later)
                    cachedWidths[tabIndex] = tabWidth;

                    tabOffset += tabWidth;
                    //If tab 'x' offset is larger than the horizontal navigation menu width
                    hiddenTabs[tabIndex] = tabOffset > navContainerRect.width;
                    //Dropdown (expandable) menu is visible if at least one menu tab is hidden
                    isMenuDropdownVisible = isMenuDropdownVisible || hiddenTabs[tabIndex];
                }
            });
            this.cachedWidths = cachedWidths;
        }

        this.hiddenTabs = hiddenTabs;
        this.isMenuDropdownVisible = isMenuDropdownVisible;

        this.bindingSignaler.signal('update-tabs');
    }

    /**
     * Toggle tabs dropdown
     * @param $event Browser event
     */
    toggleTabsDropdown($event) {
        if ($event) {
            $event.stopPropagation();
        }
        this.isMenuTabsDropdownVisible = !this.isMenuTabsDropdownVisible;
    }

    //Close 'show more' (expandable) menu
    closeMoreMenu = () => {
        if (this.isMenuDropdownVisible && this.isMenuTabsDropdownVisible)
            this.isMenuTabsDropdownVisible = false;
    }
}
